// eslint-disable-next-line import/no-unresolved
import { LockedContent } from '@creditsights/creditsights-web';
import { Icon, ICONS } from '@creditsights/styleguide-web';
import { ReactElement, useState } from 'react';
import { useQueryClient } from 'react-query';
import { NavLink } from 'react-router-dom';
import { ICON_MAP, MARKET_STATS_BONDS_ROUTE, MARKET_STATS_LOANS_ROUTE, MARKET_STATS_SPECIAL_SITUATIONS_ROUTE, MARKET_STATS_STRUCTURED_FINANCE_ROUTE } from '../../Constants';
import useFetchMarketStats from '../../hooks/market-stats/useFetchMarketStats';
import useFetchUserPermissions from '../../hooks/user/useFetchUserPermissions';
import './MarketStats.scss';
import '../../scss/header-ctx.scss';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function MarketStats({ type }): ReactElement {
  const client = useQueryClient();
  const REGIONS = ['na', 'eu'];
  const NAV_LINKS = [
    {
      title: 'Bonds',
      link: MARKET_STATS_BONDS_ROUTE,
    },
    {
      title: 'Loans',
      link: MARKET_STATS_LOANS_ROUTE,
    },
    {
      title: 'Special Situations',
      link: MARKET_STATS_SPECIAL_SITUATIONS_ROUTE,
    },
    {
      title: 'Structured Finance',
      link: MARKET_STATS_STRUCTURED_FINANCE_ROUTE,
    },
  ];

  const { data, isLoading } = useFetchMarketStats(type);
  const [active, setActive] = useState('link-na-0');
  const { data: userPermissions } = useFetchUserPermissions();

  const scrollToView = (id) => {
    const target = document.getElementById(`section-${id}`);

    if (target) {
      const parent = target.parentElement?.parentElement;
      if (parent) {
        parent.scrollTop = 0;
        const box = target.getBoundingClientRect();
        const parentBox = parent.getBoundingClientRect();
        parent.scrollTop = box.top - parentBox.top;
      }
    }

    setActive(`link-${id}`);
  };

  const renderTitles = (region) => {
    return data[region].sections.map((section, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={index} className={active === `link-${region}-${index}` ? 'link active' : 'link inactive'} onClick={() => scrollToView(`${region}-${index}`)}>
        {section.name}
      </div>
    ));
  };

  const formatUrl = (region, fileName, extension) => {
    const path = encodeURIComponent(`${region}/${type}/${fileName}.${extension}`);
    return `/api/common/v1/media/market_stats/${path}`;
  };

  const renderFiles = (files, region) => {
    // eslint-disable-next-line no-param-reassign
    files = files.filter(({ extension }) => extension !== 'png');

    return (
      <div className="files">
        {files.map((file) => (
          <a key={file.title} target="_blank" className="file" href={formatUrl(region, file.fileName, file.extension)} rel="noreferrer">
            <div className="icon">
              <Icon name={ICONS[ICON_MAP[file.extension]]} height={32} width={24} />
            </div>
            <div className="name">{file.title}</div>
          </a>
        ))}
      </div>
    );
  };

  const renderImages = (images, region) => {
    // eslint-disable-next-line no-param-reassign
    images = images.filter(({ extension }) => extension === 'png');

    return (
      <div className="images">
        {images.map((image) => (
          <div key={image.fileName} className="image">
            <img alt="" src={formatUrl(region, image.fileName, image.extension)} />
          </div>
        ))}
      </div>
    );
  };

  const renderRegion = (region) => {
    const payload = data[region];
    return (
      <div key={region} className="region-ctx">
        {payload.sections.map((section, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div id={`section-${region}-${index}`} key={index} className="section">
            <div className="title">{section.name}</div>
            {section.subtitle && <div className="sub-title">{section.subtitle}</div>}
            {renderFiles(section.files, region)}
            {renderImages(section.files, region)}
          </div>
        ))}
      </div>
    );
  };

  if (userPermissions && !userPermissions.hasAccessToMarketStats()) {
    return <LockedContent client={client} />;
  }

  return (
    <div className="market-stats-contents">
      <div className="header-ctx">
        <div className="ctx">
          <div className="header">
            <div className="title">Market Stats</div>
            <div className="separator" />
            <div className="nav-links">
              {NAV_LINKS.map((item) => (
                <NavLink key={item.title} to={item.link}>
                  {item.title}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="body-ctx">
        <div className="ctx">
          {!isLoading && (
            <div className="contents-ctx">
              <div className="navigation">
                {REGIONS.map((region) => (
                  <div key={region} className="region-ctx">
                    <div className="region-title">{region === 'na' ? 'US' : 'EMEA'}</div>
                    <div className="links">{renderTitles(region)}</div>
                  </div>
                ))}
              </div>
              <div className="contents">{REGIONS.map((region) => renderRegion(region))}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MarketStats;
